import { cn } from '@/shared/helpers/classnames'

import { type FC, createContext, useMemo } from 'react'

import type { RadioGroupProps } from './types'

type RadioGroupContextValue<T extends string | number = string> = {
  value: T
  onChange: (value: T) => void
  size: 'sm' | 'md' | 'lg'
  disabled?: boolean
  error?: boolean
  name?: string
}

export const RadioGroupContext = createContext<RadioGroupContextValue<string> | undefined>(
  undefined,
)

/**
 * RadioGroup component that manages a group of radio buttons.
 *
 * Provides compound pattern with RadioGroup.Option sub-component.
 *
 * Features:
 * - Single selection from multiple options
 * - Automatic state management
 * - Support for vertical or horizontal orientation
 * - Disabled state for entire group or individual options
 * - Error state propagation
 * - Automatic text sizing based on group size
 *
 * @example
 * Simple usage with text:
 * ```tsx
 * const [selected, setSelected] = useState('option1')
 *
 * <RadioGroup value={selected} onChange={setSelected}>
 *   <RadioGroup.Option value="option1">Option 1</RadioGroup.Option>
 *   <RadioGroup.Option value="option2">Option 2</RadioGroup.Option>
 * </RadioGroup>
 * ```
 *
 * @example
 * With complex content:
 * ```tsx
 * <RadioGroup value={selected} onChange={setSelected}>
 *   <RadioGroup.Option value="option1">
 *     <div>
 *       <div className="font-semibold">Title</div>
 *       <div className="text-sm">Description</div>
 *     </div>
 *   </RadioGroup.Option>
 * </RadioGroup>
 * ```
 */
export const RadioGroupRoot: FC<RadioGroupProps> = ({
  value,
  onChange,
  size = 'md',
  orientation = 'vertical',
  disabled = false,
  error = false,
  name = 'radiogroup',
  children,
}) => {
  const contextValue = useMemo(
    () => ({
      value,
      onChange,
      size,
      disabled,
      error,
      name,
    }),
    [value, onChange, size, disabled, error, name],
  )

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <fieldset
        className={cn('radio-group', {
          'radio-group--horizontal': orientation === 'horizontal',
          'radio-group--vertical': orientation === 'vertical',
        })}
        aria-label={name}
      >
        {children}
      </fieldset>
    </RadioGroupContext.Provider>
  )
}

RadioGroupRoot.displayName = 'RadioGroup'
